import React from 'react';
import {Editor} from 'draft-js';
import * as triggers from './Triggers';

export class CustomEditor extends Editor {
	constructor(props) {
		super(props);
		this.autocompleteState = null;

		this.onChange = editorState => {
			const {onChange, onAutocompleteChange} = this.props;
			onChange(editorState);
			if (onAutocompleteChange) {
				window.requestAnimationFrame(() => {
					onAutocompleteChange(this.getAutocompleteState());
				});
			}
		};

		this.onArrow = (e, originalHandler, nudgeAmount) => {
			const {onAutocompleteChange} = this.props;
			let autocompleteState = this.getAutocompleteState(false);
			if (!autocompleteState) {
				if (originalHandler) {
					originalHandler(e);
				}
				return;
			}

			e.preventDefault();
			autocompleteState.selectedIndex += nudgeAmount;
			this.autocompleteState = autocompleteState;
			if (onAutocompleteChange) {
				onAutocompleteChange(autocompleteState);
			}
		};

		this.onUpArrow = e => {
			this.onArrow(e, this.props.onUpArrow, -1);
		};

		this.onDownArrow = e => {
			this.onArrow(e, this.props.onDownArrow, 1);
		};

		this.onEscape = e => {
			const {onEscape, onAutocompleteChange} = this.props;

			if (!this.getAutocompleteState(false)) {
				if (onEscape) {
					onEscape(e);
				}
				return;
			}

			e.preventDefault();
			this.autocompleteState = null;

			if (onAutocompleteChange) {
				onAutocompleteChange(null);
			}
		};

		this.onTab = e => {
			this.commitSelection(e);
		};

		this.handleReturn = e => {
			return this.commitSelection(e) || this.props.handleReturn(e);
		};
	}

	commitSelection(e) {
		const {onAutocompleteChange} = this.props;
		let autocompleteState = this.getAutocompleteState(false);
		if (!autocompleteState) {
			return false;
		}
		e.preventDefault();
		this.onMentionSelect();
		this.autocompleteState = null;

		if (onAutocompleteChange) {
			onAutocompleteChange(null);
		}
		return true;
	}

	onMentionSelect() {
		let autocompleteState = this.getAutocompleteState(false);
		const insertState = this.getInsertState(autocompleteState.selectedIndex, autocompleteState.trigger);
		const {onInsert} = this.props;
		const newEditorState = onInsert(insertState);
		const {onChange} = this.props;
		onChange(newEditorState);
	}

	getInsertState(selectedIndex, trigger) {
		const {editorState} = this.props;
		const currentSelectionState = editorState.getSelection();
		const end = currentSelectionState.getAnchorOffset();
		const anchorKey = currentSelectionState.getAnchorKey();
		const currentContent = editorState.getCurrentContent();
		const currentBlock = currentContent.getBlockForKey(anchorKey);
		const blockText = currentBlock.getText();
		const start = blockText.substring(0, end).lastIndexOf(trigger);
		return {
			editorState,
			start,
			end,
			trigger,
			selectedIndex,
		};
	}

	getAutocompleteState(invalidate = true) {
		if (!invalidate) {
			return this.autocompleteState;
		}
		var type = null;
		var trigger = null;
		//Get range for latest hash tag trigger symbol.
		const tagRange = this.getAutocompleteRange(triggers.TAG_TRIGGER);
		//Get range for latest mention tag trigger symbol.
		const personRange = this.getAutocompleteRange(triggers.PERSON_TRIGGER);
		//Find what trigger is latest.
		if (!tagRange && !personRange) {
			this.autocompleteState = null;
			return null;
		}
		var range = null;
		if (!tagRange) {
			range = personRange;
			type = triggers.PERSON;
			trigger = triggers.PERSON_TRIGGER;
		}
		if (!personRange) {
			range = tagRange;
			type = triggers.TAG;
			trigger = triggers.TAG_TRIGGER;
		}
		if (!range) {
			range = tagRange.start > personRange.start ? tagRange : personRange;
			type = tagRange.start > personRange.start ? triggers.TAG : triggers.PERSON;
			trigger = tagRange.start > personRange.start ? triggers.TAG_TRIGGER : triggers.PERSON_TRIGGER;
		}
		//Get left and top coordinates of range.
		//This point will be used to draw suggestion list.
		const tempRange = window.getSelection().getRangeAt(0).cloneRange();
		tempRange.setStart(tempRange.startContainer, range.start);
		const rangeRect = tempRange.getBoundingClientRect();
		let [left, top] = [rangeRect.left, rangeRect.bottom];
		//Create autocompleteState.
		this.autocompleteState = {
			trigger, //Trigger symbol. “@” or “#”
			type, //Type of trigger. Can be TAG or PERSON.
			left, //The left point of range.
			top, //The top point of range.
			text: range.text, //Current text in selected range.
			selectedIndex: 0, //Selected index in list. 0 for new list.
		};
		return this.autocompleteState;
	}
	//Get range of possible mention or hashtag.
	getAutocompleteRange(trigger) {
		const selection = window.getSelection();
		if (selection.rangeCount === 0) {
			return null;
		}
		if (this.hasEntityAtSelection()) {
			return null;
		}
		const range = selection.getRangeAt(0);
		let text = range.startContainer.textContent;
		text = text.substring(0, range.startOffset);
		const index = text.lastIndexOf(trigger);
		if (index === -1) {
			return null;
		}
		text = text.substring(index);
		return {
			text,
			start: index,
			end: range.startOffset,
		};
	}

	hasEntityAtSelection() {
		const {editorState} = this.props;
		const selection = editorState.getSelection();
		//If there is no focus, return.
		if (!selection.getHasFocus()) {
			return false;
		}
		const contentState = editorState.getCurrentContent();
		const block = contentState.getBlockForKey(selection.getStartKey());
		return !!block.getEntityAt(selection.getStartOffset() - 1);
	}

	render() {
		// eslint-disable-next-line no-unused-vars
		const {onChange, editorState, handleReturn, ...rest} = this.props;
		return <Editor editorState={editorState} onChange={this.onChange} onTab={this.onTab} handleReturn={this.handleReturn} onEscape={this.onEscape} onUpArrow={this.onUpArrow} onDownArrow={this.onDownArrow} {...rest} />;
	}
}
